import { useAuth } from "../hooks/useAuth";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { MainLayout } from "../components/layout";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { leaveService } from "../services/leave.service";
import { AttendanceTodaySummaryCard } from "../components/features/attendance/AttendanceTodaySummaryCard";
import {
  getThaiFiscalYear,
  getThaiFiscalYearDateRange,
} from "@leave-system/shared/utils";

export function Dashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const monthsToShow = 6;

  // Thai Fiscal Year
  const currentFiscalYearBE = getThaiFiscalYear();
  const fiscalYearRange = getThaiFiscalYearDateRange(currentFiscalYearBE);

  // Fetch dashboard stats
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => leaveService.getDashboardStats(),
    enabled: !!user,
  });

  // Fetch monthly trend
  const {
    data: monthlyTrend,
    isLoading: trendLoading,
    error: trendError,
  } = useQuery({
    queryKey: ["monthly-trend", monthsToShow],
    queryFn: () => leaveService.getMonthlyTrend(monthsToShow),
    enabled: !!user,
  });

  // Fetch conflicting leaves
  const {
    data: conflictingLeaves,
    isLoading: conflictsLoading,
    error: conflictsError,
  } = useQuery({
    queryKey: ["conflicting-leaves"],
    queryFn: () => leaveService.getConflictingLeaves(),
    enabled: !!user,
  });

  // Fetch employee balances
  const {
    data: employeeBalances,
    isLoading: balancesLoading,
    error: balancesError,
  } = useQuery({
    queryKey: ["employee-balances"],
    queryFn: () => leaveService.getEmployeeBalances(),
    enabled: !!user,
  });

  // Set first employee as default when data loads
  useEffect(() => {
    if (employeeBalances && employeeBalances.length > 0 && !selectedEmployee) {
      setSelectedEmployee(employeeBalances[0].id);
    }
  }, [employeeBalances, selectedEmployee]);

  // Calculate pie chart data from stats
  const pieData = stats?.thisYear
    ? [
        { name: "ลาป่วย", value: stats.thisYear.sick, color: "#ef4444" },
        { name: "ลาพักร้อน", value: stats.thisYear.vacation, color: "#3b82f6" },
        { name: "ลากิจ", value: stats.thisYear.personal, color: "#f59e0b" },
      ]
    : [];

  const totalLeaveDays = stats?.thisYear
    ? stats.thisYear.sick + stats.thisYear.vacation + stats.thisYear.personal
    : 0;

  const selectedEmpData = employeeBalances?.find(
    (emp) => emp.id === selectedEmployee
  );

  const isAnyLoading =
    authLoading ||
    statsLoading ||
    trendLoading ||
    conflictsLoading ||
    balancesLoading;

  if (isAnyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    window.location.href = "/login";
    return null;
  }

  if (statsError || trendError || conflictsError || balancesError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600 mb-4">
            เกิดข้อผิดพลาดในการโหลดข้อมูล
          </p>
          <Button onClick={() => window.location.reload()}>โหลดใหม่</Button>
        </div>
      </div>
    );
  }

  return (
    <MainLayout title="📊 Dashboard - ภาพรวมระบบ">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AttendanceTodaySummaryCard />
        </div>

        {/* 1. สถิติการลา - วันนี้ สัปดาห์นี้ เดือนนี้ */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">📊 สถิติการลา</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* วันนี้ */}
            <Card>
              <CardHeader>
                <CardTitle>วันนี้</CardTitle>
                <CardDescription>การลาในวันนี้</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-blue-600 mb-4">
                  {stats?.today?.total || 0}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">🤒 ลาป่วย</span>
                    <span className="font-semibold">
                      {stats?.today?.sick || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">🏖️ ลาพักร้อน</span>
                    <span className="font-semibold">
                      {stats?.today?.vacation || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">📋 ลากิจ</span>
                    <span className="font-semibold">
                      {stats?.today?.personal || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* สัปดาห์นี้ */}
            <Card>
              <CardHeader>
                <CardTitle>สัปดาห์นี้</CardTitle>
                <CardDescription>การลาในสัปดาห์นี้</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-green-600 mb-4">
                  {stats?.thisWeek?.total || 0}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">🤒 ลาป่วย</span>
                    <span className="font-semibold">
                      {stats?.thisWeek?.sick || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">🏖️ ลาพักร้อน</span>
                    <span className="font-semibold">
                      {stats?.thisWeek?.vacation || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">📋 ลากิจ</span>
                    <span className="font-semibold">
                      {stats?.thisWeek?.personal || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* เดือนนี้ */}
            <Card>
              <CardHeader>
                <CardTitle>เดือนนี้</CardTitle>
                <CardDescription>การลาในเดือนนี้</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-purple-600 mb-4">
                  {stats?.thisMonth?.total || 0}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">🤒 ลาป่วย</span>
                    <span className="font-semibold">
                      {stats?.thisMonth?.sick || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">🏖️ ลาพักร้อน</span>
                    <span className="font-semibold">
                      {stats?.thisMonth?.vacation || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">📋 ลากิจ</span>
                    <span className="font-semibold">
                      {stats?.thisMonth?.personal || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* 2. ภาพรวมทั้งหมด + 3. กราฟวงกลม */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* ภาพรวมทั้งหมด */}
          <Card>
            <CardHeader>
              <CardTitle>
                📈 ภาพรวมการลาปีงบประมาณ {currentFiscalYearBE}
              </CardTitle>
              <CardDescription>
                สรุปการลาตั้งแต่ {fiscalYearRange.startDateString} ถึงปัจจุบัน
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <div className="text-5xl font-bold text-blue-600 mb-2">
                  {totalLeaveDays}
                </div>
                <p className="text-gray-600">วันลาทั้งหมด</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span className="font-medium">ลาป่วย</span>
                  </div>
                  <span className="text-2xl font-bold text-red-600">
                    {stats?.thisYear?.sick || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span className="font-medium">ลาพักร้อน</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">
                    {stats?.thisYear?.vacation || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-500 rounded"></div>
                    <span className="font-medium">ลากิจ</span>
                  </div>
                  <span className="text-2xl font-bold text-orange-600">
                    {stats?.thisYear?.personal || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* กราฟวงกลม - สัดส่วนการลา */}
          <Card>
            <CardHeader>
              <CardTitle>🥧 สัดส่วนการลาแต่ละประเภท</CardTitle>
              <CardDescription>การกระจายของประเภทการลา</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(props: any) => {
                      const { name, percent } = props;
                      return `${name} ${(percent * 100).toFixed(0)}%`;
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* 4. กราฟแนวโน้มรายเดือน */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>📉 แนวโน้มการลารายเดือน</CardTitle>
            <CardDescription>สถิติการลาย้อนหลัง 6 เดือน</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sick"
                  stroke="#ef4444"
                  name="ลาป่วย"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="vacation"
                  stroke="#3b82f6"
                  name="ลาพักร้อน"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="personal"
                  stroke="#f59e0b"
                  name="ลากิจ"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* 5. วันลาที่ชนกัน + 6. วันลาคงเหลือของพนักงาน */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* วันลาที่ชนกัน */}
          <Card>
            <CardHeader>
              <CardTitle>⚠️ วันลาที่ชนกัน</CardTitle>
              <CardDescription>พนักงานที่ลาวันเดียวกัน</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {conflictingLeaves && conflictingLeaves.length > 0 ? (
                  conflictingLeaves.map((conflict, index) => (
                    <div
                      key={index}
                      className="p-4 border border-orange-200 bg-orange-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">📅</span>
                        <span className="font-semibold text-orange-800">
                          {conflict.date}
                        </span>
                        <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs rounded-full">
                          {conflict.count} คน
                        </span>
                      </div>
                      <div className="space-y-1 ml-7">
                        {conflict.employees.map((emp, empIndex) => (
                          <div key={empIndex} className="text-sm text-gray-700">
                            • {emp}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-4">
                    ไม่มีวันลาที่ชนกัน
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* วันลาคงเหลือของพนักงาน */}
          <Card>
            <CardHeader>
              <CardTitle>👤 วันลาคงเหลือ</CardTitle>
              <CardDescription>ตรวจสอบวันลาคงเหลือของพนักงาน</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                  เลือกพนักงาน
                </label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {employeeBalances && employeeBalances.length > 0 ? (
                    employeeBalances.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))
                  ) : (
                    <option value="">ไม่มีข้อมูลพนักงาน</option>
                  )}
                </select>
              </div>

              {selectedEmpData ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>🤒 ลาป่วย</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-red-600">
                        {selectedEmpData.sick}
                      </div>
                      <div className="text-xs text-gray-500">วันคงเหลือ</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>🏖️ ลาพักร้อน</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedEmpData.vacation}
                      </div>
                      <div className="text-xs text-gray-500">วันคงเหลือ</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>📋 ลากิจ</span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-orange-600">
                        {selectedEmpData.personal}
                      </div>
                      <div className="text-xs text-gray-500">วันคงเหลือ</div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  กรุณาเลือกพนักงาน
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}

export default Dashboard;
